import React, { createContext, useContext, useEffect, useState } from "react";

const translations = {
  en: {
    // brand / generic
    "app.platform": "MilkERP Platform",
    "app.multiTenant": "Multi-Tenant SaaS",
    "app.dairyErp": "Dairy ERP",
    "common.add": "Add",
    "common.save": "Save",
    "common.saveChanges": "Save Changes",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.create": "Create",
    "common.update": "Update",
    "common.search": "Search",
    "common.actions": "Actions",
    "common.action": "Action",
    "common.status": "Status",
    "common.active": "Active",
    "common.disabled": "Disabled",
    "common.prev": "Prev",
    "common.next": "Next",
    "common.page": "Page",
    "common.of": "of",
    "common.records": "records",
    "common.print": "Print",
    "common.excel": "Excel",
    "common.pdf": "PDF",
    "common.close": "Close",
    "common.all": "All",
    "common.allStatuses": "All statuses",
    "common.allShifts": "All shifts",
    "common.noData": "No data.",
    "common.logout": "Log out",
    "common.language": "Language",
    "common.morning": "Morning",
    "common.evening": "Evening",
    "common.cow": "Cow",
    "common.buffalo": "Buffalo",
    "common.manage": "Manage",
    "common.current": "Current",
    "common.time": "Time",
    "common.user": "User",
    "common.details": "Details",
    "common.entity": "Entity",
    "common.date": "Date",
    "common.from": "From",
    "common.to": "To",
    "common.amount": "Amount",
    "common.quantity": "Quantity",
    "common.qty": "Qty",
    "common.mobile": "Mobile",
    "common.email": "Email",
    "common.name": "Name",
    "common.role": "Role",
    "common.permissions": "Permissions",
    // roles
    "role.super_admin": "Super Admin",
    "role.tenant_owner": "Owner",
    "role.operator": "Operator",
    "role.accountant": "Accountant",
    // nav
    "nav.dashboard": "Dashboard",
    "nav.tenants": "Tenants",
    "nav.plans": "Subscription Plans",
    "nav.farmers": "Farmers",
    "nav.collections": "Milk Collection",
    "nav.rateChart": "Rate Chart",
    "nav.payments": "Payments",
    "nav.reports": "Reports",
    "nav.feed": "Cattle Feed",
    "feed.title": "Cattle Feed",
    "feed.subtitle": "Manage feed stock, sales and deductions",
    "feed.tab.inventory": "Inventory",
    "feed.tab.sales": "Sales",
    "feed.tab.summary": "Monthly Summary",
    "feed.tab.statement": "Farmer Statement",
    "feed.rate": "Rate/Bag (₹)",
    "feed.lineTotal": "Line Total",
    "feed.saleTotal": "Sale Total",
    "feed.addItem": "Add Feed Item",
    "feed.item": "Item",
    "feed.selectFarmerStatement": "Select a farmer to view their feed statement",
    "feed.salesHistory": "Feed Sales",
    "feed.deductionsList": "Deductions Collected",
    "feed.totalPaid": "Total Paid",
    "feed.totalDeducted": "Total Deducted",
    "feed.amount": "Amount",
    "feed.paid": "Paid",
    "feed.noDeductions": "No deductions yet",
    "feed.addStock": "Add Stock",
    "feed.recordSale": "Record Sale",
    "feed.type": "Feed Type",
    "feed.qty": "Quantity (bags)",
    "feed.unitCost": "Rate per Bag (₹)",
    "feed.threshold": "Low Stock Alert (bags)",
    "feed.date": "Date",
    "feed.currentStock": "Current Stock",
    "feed.lowStock": "Low Stock",
    "feed.inStock": "In Stock",
    "feed.recentEntries": "Recent Stock Entries",
    "feed.totalCost": "Total Cost (₹)",
    "feed.status": "Payment",
    "feed.netPay": "Net Pay",
    "feed.pending": "Pending Payment",
    "feed.farmer": "Farmer",
    "feed.noStock": "No stock entries yet",
    "feed.noSales": "No sales yet",
    "feed.month": "Month",
    "feed.stockAdded": "Stock Added",
    "feed.totalSales": "Total Sales",
    "feed.deductionsCollected": "Deductions Collected",
    "feed.pendingBalance": "Pending Feed Balance",
    "feed.applyDeduction": "Apply Feed Deduction",
    "feed.deductionAmount": "Feed Deduction (₹)",
    "feed.pendingAlert": "This farmer has an outstanding feed balance of",
    "feed.saleRecorded": "Sale recorded",
    "feed.stockAddedToast": "Stock added",
    "feed.settled": "Settled",
    "feed.receipt": "Receipt",
    "feed.qtyShort": "Qty",
    "feed.selectType": "Select or type a feed type",
    "feed.bags": "bags",
    "nav.users": "Users",
    "nav.activity": "Activity Logs",
    "nav.settings": "Settings",
    "nav.subscription": "Subscription",
    "nav.billing": "Billing",
    "crumb.platform": "Platform",
    "crumb.center": "Center",
    // login
    "login.welcome": "Welcome back",
    "login.subtitle": "Sign in to your account to continue.",
    "login.email": "Email",
    "login.password": "Password",
    "login.remember": "Remember me",
    "login.forgot": "Forgot password?",
    "login.signIn": "Sign in",
    "login.demo": "Demo accounts (click to fill)",
    "login.heroTitle": "Run every collection center from one platform.",
    "login.heroSubtitle": "Multi-tenant dairy ERP for milk unions and private collection centers — farmers, collections, payments, and reports, fully isolated per center.",
    // forgot / reset
    "forgot.title": "Forgot password?",
    "forgot.subtitle": "Enter your email to receive a reset link.",
    "forgot.send": "Send reset link",
    "forgot.back": "Back to login",
    "forgot.checkTitle": "Check your email",
    "reset.title": "Set a new password",
    "reset.subtitle": "Enter and confirm your new password.",
    "reset.new": "New password",
    "reset.confirm": "Confirm password",
    "reset.update": "Update password",
    // platform dashboard
    "pd.title": "Platform Overview",
    "pd.subtitle": "Cross-tenant metrics and subscription analytics",
    "pd.totalTenants": "Total Tenants",
    "pd.active": "Active",
    "pd.trial": "Trial",
    "pd.expired": "Expired",
    "pd.monthlyRevenue": "Monthly Revenue",
    "pd.totalCollections": "Total Collections",
    "pd.totalFarmers": "Total Farmers",
    "pd.storage": "Storage Used",
    "pd.growth": "Tenant Growth",
    "pd.plans": "Subscription Plans",
    "pd.statusDist": "Tenant Status Distribution",
    // tenants
    "tenants.title": "Tenants",
    "tenants.subtitle": "Milk collection centers on the platform",
    "tenants.new": "New Tenant",
    "tenants.create": "Create Milk Collection Center",
    "tenants.center": "Center",
    "tenants.owner": "Owner",
    "tenants.plan": "Plan",
    "tenants.farmers": "Farmers",
    "tenants.searchPlaceholder": "Search company, center, owner, subdomain…",
    "tenants.none": "No tenants found.",
    "tenants.createBtn": "Create Tenant",
    // tenant detail
    "td.back": "Back to tenants",
    "td.activate": "Activate",
    "td.suspend": "Suspend",
    "td.resetPassword": "Reset Password",
    "td.subscription": "Subscription",
    "td.currentPlan": "Current Plan",
    "td.trialEnds": "Trial ends",
    "td.subEnds": "Subscription ends",
    "td.created": "Created",
    "td.ownerContact": "Owner & Contact",
    "td.users": "Users",
    // plans
    "plans.title": "Subscription Plans",
    "plans.subtitle": "Feature limits per tier",
    "plans.popular": "Popular",
    "plans.month": "/month",
    "plans.maxUsers": "Max Users",
    "plans.maxFarmers": "Max Farmers",
    "plans.maxDaily": "Max Daily Collections",
    "plans.storage": "Storage (MB)",
    "plans.support": "Support Priority",
    "plans.export": "PDF & Excel Export",
    "plans.api": "API Access",
    // tenant dashboard
    "dash.title": "Dashboard",
    "dash.todayMilk": "Today's Milk",
    "dash.todayAmount": "Today's Amount",
    "dash.todayFarmers": "Today's Farmers",
    "dash.morning": "Morning",
    "dash.evening": "Evening",
    "dash.pending": "Pending Payments",
    "dash.totalFarmers": "Total Farmers",
    "dash.daily": "Daily Collection (last 14 days)",
    "dash.milkType": "Milk Type Split",
    "dash.monthly": "Monthly Collection vs Amount",
    "dash.recent": "Recent Collections",
    "dash.noCollections": "No collections yet.",
    "dash.litres": "Litres",
    // farmers
    "farmers.title": "Farmers",
    "farmers.subtitle": "Manage member farmers of your collection center",
    "farmers.add": "Add Farmer",
    "farmers.edit": "Edit Farmer",
    "farmers.farmer": "Farmer",
    "farmers.code": "Code",
    "farmers.village": "Village",
    "farmers.searchPlaceholder": "Search name, code, mobile, village…",
    "farmers.none": "No farmers found.",
    // collections
    "col.title": "Milk Collection",
    "col.subtitle": "Record morning & evening collections with auto rate calculation",
    "col.newEntry": "New Entry",
    "col.shift": "Shift",
    "col.milkType": "Milk Type",
    "col.quantity": "Quantity (L)",
    "col.rate": "Rate / L",
    "col.savePrint": "Save & Print Receipt",
    "col.history": "Collection History",
    "col.no": "No.",
    "col.selectFarmer": "Select farmer",
    "col.none": "No collections found.",
    "col.receipt": "Receipt",
    // rate chart
    "rate.title": "Rate Chart",
    "rate.subtitle": "Amount = Base + (FAT × FAT rate) + (SNF × SNF rate), per litre. Latest effective date applies automatically.",
    "rate.new": "New Rate",
    "rate.effectiveDate": "Effective Date",
    "rate.base": "Base Rate ₹",
    "rate.fat": "FAT Rate ₹",
    "rate.snf": "SNF Rate ₹",
    "rate.cowRates": "Cow Milk Rates",
    "rate.buffaloRates": "Buffalo Milk Rates",
    "rate.none": "No rates yet.",
    // payments
    "pay.title": "Payments",
    "pay.subtitle": "Generate farmer payments with advance, deduction and bonus",
    "pay.generate": "Generate Payment",
    "pay.period": "Period",
    "pay.gross": "Gross",
    "pay.deductions": "Deductions",
    "pay.net": "Net",
    "pay.advance": "Advance ₹",
    "pay.deduction": "Deduction ₹",
    "pay.bonus": "Bonus ₹",
    "pay.grossUnpaid": "Gross (unpaid)",
    "pay.collections": "Collections",
    "pay.netPayable": "Net Payable",
    "pay.none": "No payments yet.",
    "pay.receipt": "Payment Receipt",
    // reports
    "rep.title": "Reports",
    "rep.subtitle": "Collection, farmer-wise, quality and payment analytics",
    "rep.totalMilk": "Total Milk",
    "rep.totalAmount": "Total Amount",
    "rep.entries": "Entries",
    "rep.summary": "Collection Summary",
    "rep.farmerWise": "Farmer-wise",
    "rep.quality": "FAT / SNF",
    "rep.payments": "Payments",
    "rep.daily": "Daily",
    "rep.monthly": "Monthly",
    "rep.avgFat": "Avg FAT",
    "rep.avgSnf": "Avg SNF",
    "rep.qualityTrend": "Average FAT & SNF Trend",
    "rep.paymentReport": "Payment Report",
    "rep.ledger": "Farmer Ledger",
    "rep.ledgerReport": "Farmer Payment Ledger",
    "sms.send": "Send SMS",
    "sms.openApp": "Open SMS App",
    "sms.preview": "SMS Preview",
    "sms.to": "To",
    "sms.note": "On pressing Send, the SMS is sent from your own SIM and SMS pack.",
    "sms.desktop": "To send SMS, please open Dairy Hub ERP on mobile.",
    "sms.invalid": "This farmer does not have a valid mobile number.",
    // users
    "users.title": "User Management",
    "users.subtitle": "Invite operators and accountants with role-based permissions",
    "users.add": "Add User",
    "users.create": "Create User",
    "users.password": "Password",
    "perm.tenant_owner": "Full access to all modules",
    "perm.operator": "Add collections · Print receipts · View farmers",
    "perm.accountant": "Payments · Reports · Export · View collections",
    // activity
    "act.title": "Activity Logs",
    "act.subtitle": "Audit trail of all actions in your center",
    "act.none": "No activity yet.",
    // settings
    "set.title": "Settings",
    "set.companyAppearance": "Company & Appearance",
    "set.companyName": "Company Name",
    "set.logo": "Logo URL",
    "set.theme": "Theme",
    "set.light": "Light",
    "set.dark": "Dark",
    "set.currency": "Currency",
    "set.printer": "Thermal Printer",
    "set.paperWidth": "Paper Width",
    "set.header": "Receipt Header",
    "set.footer": "Receipt Footer",
    "set.notifications": "Notifications",
    "set.emailActive": "Email (active)",
    "set.smsSoon": "SMS (coming soon)",
    "set.whatsappSoon": "WhatsApp (coming soon)",
    "set.pushSoon": "Push (coming soon)",
    "receipt.milkTitle": "MILK COLLECTION RECEIPT",
    "receipt.paymentTitle": "PAYMENT RECEIPT",
    "receipt.receipt": "Receipt",
    "receipt.date": "Date",
    "receipt.farmer": "Farmer",
    "receipt.type": "Type",
    "receipt.qty": "Qty (L)",
    "receipt.rate": "Rate/L",
    "receipt.total": "TOTAL",
    "receipt.thanks": "Thank you!",
    "receipt.gross": "Gross",
    "receipt.advance": "Advance",
    "receipt.deduction": "Deduction",
    "receipt.bonus": "Bonus",
    "receipt.netPaid": "NET PAID",
    "receipt.period": "Period",
  },
  mr: {
    "app.platform": "मिल्कईआरपी प्लॅटफॉर्म",
    "app.multiTenant": "मल्टी-टेनंट सास",
    "app.dairyErp": "डेअरी ईआरपी",
    "common.add": "जोडा",
    "common.save": "जतन करा",
    "common.saveChanges": "बदल जतन करा",
    "common.cancel": "रद्द करा",
    "common.delete": "हटवा",
    "common.edit": "संपादित करा",
    "common.create": "तयार करा",
    "common.update": "अद्ययावत करा",
    "common.search": "शोधा",
    "common.actions": "क्रिया",
    "common.action": "क्रिया",
    "common.status": "स्थिती",
    "common.active": "सक्रिय",
    "common.disabled": "अक्षम",
    "common.prev": "मागील",
    "common.next": "पुढील",
    "common.page": "पृष्ठ",
    "common.of": "पैकी",
    "common.records": "नोंदी",
    "common.print": "प्रिंट करा",
    "common.excel": "एक्सेल",
    "common.pdf": "पीडीएफ",
    "common.close": "बंद करा",
    "common.all": "सर्व",
    "common.allStatuses": "सर्व स्थिती",
    "common.allShifts": "सर्व पाळ्या",
    "common.noData": "माहिती नाही.",
    "common.logout": "बाहेर पडा",
    "common.language": "भाषा",
    "common.morning": "सकाळ",
    "common.evening": "संध्याकाळ",
    "common.cow": "गाय",
    "common.buffalo": "म्हैस",
    "common.manage": "व्यवस्थापन",
    "common.current": "सध्याचा",
    "common.time": "वेळ",
    "common.user": "वापरकर्ता",
    "common.details": "तपशील",
    "common.entity": "घटक",
    "common.date": "तारीख",
    "common.from": "पासून",
    "common.to": "पर्यंत",
    "common.amount": "रक्कम",
    "common.quantity": "प्रमाण",
    "common.qty": "प्रमाण",
    "common.mobile": "मोबाइल",
    "common.email": "ईमेल",
    "common.name": "नाव",
    "common.role": "भूमिका",
    "common.permissions": "परवानग्या",
    "role.super_admin": "सुपर अ‍ॅडमिन",
    "role.tenant_owner": "मालक",
    "role.operator": "ऑपरेटर",
    "role.accountant": "लेखापाल",
    "nav.dashboard": "डॅशबोर्ड",
    "nav.tenants": "केंद्रे",
    "nav.plans": "सदस्यता योजना",
    "nav.farmers": "शेतकरी",
    "nav.collections": "दूध संकलन",
    "nav.rateChart": "दर तक्ता",
    "nav.payments": "देयके",
    "nav.reports": "अहवाल",
    "nav.feed": "पशुखाद्य",
    "feed.title": "पशुखाद्य",
    "feed.subtitle": "खाद्य साठा, विक्री व वजावट व्यवस्थापित करा",
    "feed.tab.inventory": "साठा",
    "feed.tab.sales": "विक्री",
    "feed.tab.summary": "मासिक सारांश",
    "feed.tab.statement": "शेतकरी विवरण",
    "feed.rate": "प्रति गोणी दर (₹)",
    "feed.lineTotal": "एकूण",
    "feed.saleTotal": "विक्री एकूण",
    "feed.addItem": "खाद्य वस्तू जोडा",
    "feed.item": "वस्तू",
    "feed.selectFarmerStatement": "खाद्य विवरण पाहण्यासाठी शेतकरी निवडा",
    "feed.salesHistory": "खाद्य विक्री",
    "feed.deductionsList": "वसूल वजावट",
    "feed.totalPaid": "एकूण भरणा",
    "feed.totalDeducted": "एकूण वजावट",
    "feed.amount": "रक्कम",
    "feed.paid": "भरले",
    "feed.noDeductions": "अद्याप वजावट नाही",
    "feed.addStock": "साठा जोडा",
    "feed.recordSale": "विक्री नोंदवा",
    "feed.type": "खाद्य प्रकार",
    "feed.qty": "प्रमाण (गोणी)",
    "feed.unitCost": "प्रति गोणी दर (₹)",
    "feed.threshold": "कमी साठा इशारा (गोणी)",
    "feed.date": "दिनांक",
    "feed.currentStock": "सध्याचा साठा",
    "feed.lowStock": "कमी साठा",
    "feed.inStock": "उपलब्ध",
    "feed.recentEntries": "अलीकडील साठा नोंदी",
    "feed.totalCost": "एकूण किंमत (₹)",
    "feed.status": "देयक",
    "feed.netPay": "रोख भरणा",
    "feed.pending": "प्रलंबित देयक",
    "feed.farmer": "शेतकरी",
    "feed.noStock": "अद्याप साठा नोंद नाही",
    "feed.noSales": "अद्याप विक्री नाही",
    "feed.month": "महिना",
    "feed.stockAdded": "जोडलेला साठा",
    "feed.totalSales": "एकूण विक्री",
    "feed.deductionsCollected": "वसूल वजावट",
    "feed.pendingBalance": "प्रलंबित खाद्य शिल्लक",
    "feed.applyDeduction": "खाद्य वजावट लागू करा",
    "feed.deductionAmount": "खाद्य वजावट (₹)",
    "feed.pendingAlert": "या शेतकऱ्याची प्रलंबित खाद्य शिल्लक आहे",
    "feed.saleRecorded": "विक्री नोंदवली",
    "feed.stockAddedToast": "साठा जोडला",
    "feed.settled": "निकाली",
    "feed.receipt": "पावती",
    "feed.qtyShort": "प्रमाण",
    "feed.selectType": "खाद्य प्रकार निवडा किंवा टाइप करा",
    "feed.bags": "गोणी",
    "nav.users": "वापरकर्ते",
    "nav.activity": "क्रियाकलाप नोंदी",
    "nav.settings": "सेटिंग्ज",
    "nav.subscription": "सदस्यता",
    "nav.billing": "बिलिंग",
    "crumb.platform": "प्लॅटफॉर्म",
    "crumb.center": "केंद्र",
    "login.welcome": "पुन्हा स्वागत आहे",
    "login.subtitle": "सुरू ठेवण्यासाठी आपल्या खात्यात साइन इन करा.",
    "login.email": "ईमेल",
    "login.password": "पासवर्ड",
    "login.remember": "मला लक्षात ठेवा",
    "login.forgot": "पासवर्ड विसरलात?",
    "login.signIn": "साइन इन करा",
    "login.demo": "डेमो खाती (भरण्यासाठी क्लिक करा)",
    "login.heroTitle": "प्रत्येक संकलन केंद्र एकाच प्लॅटफॉर्मवरून चालवा.",
    "login.heroSubtitle": "दूध संघ आणि खाजगी संकलन केंद्रांसाठी मल्टी-टेनंट डेअरी ईआरपी — शेतकरी, संकलन, देयके आणि अहवाल, प्रत्येक केंद्रासाठी पूर्णपणे वेगळे.",
    "forgot.title": "पासवर्ड विसरलात?",
    "forgot.subtitle": "रीसेट लिंक मिळवण्यासाठी आपला ईमेल टाका.",
    "forgot.send": "रीसेट लिंक पाठवा",
    "forgot.back": "लॉगिनकडे परत",
    "forgot.checkTitle": "आपला ईमेल तपासा",
    "reset.title": "नवीन पासवर्ड सेट करा",
    "reset.subtitle": "आपला नवीन पासवर्ड टाका आणि पुष्टी करा.",
    "reset.new": "नवीन पासवर्ड",
    "reset.confirm": "पासवर्डची पुष्टी करा",
    "reset.update": "पासवर्ड अद्ययावत करा",
    "pd.title": "प्लॅटफॉर्म आढावा",
    "pd.subtitle": "सर्व केंद्रांचे मेट्रिक्स आणि सदस्यता विश्लेषण",
    "pd.totalTenants": "एकूण केंद्रे",
    "pd.active": "सक्रिय",
    "pd.trial": "चाचणी",
    "pd.expired": "कालबाह्य",
    "pd.monthlyRevenue": "मासिक महसूल",
    "pd.totalCollections": "एकूण संकलन",
    "pd.totalFarmers": "एकूण शेतकरी",
    "pd.storage": "वापरलेली साठवण",
    "pd.growth": "केंद्रांची वाढ",
    "pd.plans": "सदस्यता योजना",
    "pd.statusDist": "केंद्र स्थिती वितरण",
    "tenants.title": "केंद्रे",
    "tenants.subtitle": "प्लॅटफॉर्मवरील दूध संकलन केंद्रे",
    "tenants.new": "नवीन केंद्र",
    "tenants.create": "दूध संकलन केंद्र तयार करा",
    "tenants.center": "केंद्र",
    "tenants.owner": "मालक",
    "tenants.plan": "योजना",
    "tenants.farmers": "शेतकरी",
    "tenants.searchPlaceholder": "कंपनी, केंद्र, मालक, सबडोमेन शोधा…",
    "tenants.none": "कोणतेही केंद्र सापडले नाही.",
    "tenants.createBtn": "केंद्र तयार करा",
    "td.back": "केंद्रांकडे परत",
    "td.activate": "सक्रिय करा",
    "td.suspend": "स्थगित करा",
    "td.resetPassword": "पासवर्ड रीसेट करा",
    "td.subscription": "सदस्यता",
    "td.currentPlan": "सध्याची योजना",
    "td.trialEnds": "चाचणी संपते",
    "td.subEnds": "सदस्यता संपते",
    "td.created": "तयार केले",
    "td.ownerContact": "मालक आणि संपर्क",
    "td.users": "वापरकर्ते",
    "plans.title": "सदस्यता योजना",
    "plans.subtitle": "प्रत्येक स्तरानुसार वैशिष्ट्य मर्यादा",
    "plans.popular": "लोकप्रिय",
    "plans.month": "/महिना",
    "plans.maxUsers": "कमाल वापरकर्ते",
    "plans.maxFarmers": "कमाल शेतकरी",
    "plans.maxDaily": "कमाल दैनिक संकलन",
    "plans.storage": "साठवण (एमबी)",
    "plans.support": "समर्थन प्राधान्य",
    "plans.export": "पीडीएफ आणि एक्सेल निर्यात",
    "plans.api": "एपीआय प्रवेश",
    "dash.title": "डॅशबोर्ड",
    "dash.todayMilk": "आजचे दूध",
    "dash.todayAmount": "आजची रक्कम",
    "dash.todayFarmers": "आजचे शेतकरी",
    "dash.morning": "सकाळ",
    "dash.evening": "संध्याकाळ",
    "dash.pending": "प्रलंबित देयके",
    "dash.totalFarmers": "एकूण शेतकरी",
    "dash.daily": "दैनिक संकलन (मागील १४ दिवस)",
    "dash.milkType": "दूध प्रकार विभागणी",
    "dash.monthly": "मासिक संकलन विरुद्ध रक्कम",
    "dash.recent": "अलीकडील संकलन",
    "dash.noCollections": "अद्याप संकलन नाही.",
    "dash.litres": "लिटर",
    "farmers.title": "शेतकरी",
    "farmers.subtitle": "आपल्या संकलन केंद्रातील सदस्य शेतकरी व्यवस्थापित करा",
    "farmers.add": "शेतकरी जोडा",
    "farmers.edit": "शेतकरी संपादित करा",
    "farmers.farmer": "शेतकरी",
    "farmers.code": "कोड",
    "farmers.village": "गाव",
    "farmers.searchPlaceholder": "नाव, कोड, मोबाइल, गाव शोधा…",
    "farmers.none": "कोणताही शेतकरी सापडला नाही.",
    "col.title": "दूध संकलन",
    "col.subtitle": "स्वयंचलित दर गणनेसह सकाळ आणि संध्याकाळचे संकलन नोंदवा",
    "col.newEntry": "नवीन नोंद",
    "col.shift": "पाळी",
    "col.milkType": "दूध प्रकार",
    "col.quantity": "प्रमाण (लि)",
    "col.rate": "दर / लि",
    "col.savePrint": "जतन करा व पावती प्रिंट करा",
    "col.history": "संकलन इतिहास",
    "col.no": "क्र.",
    "col.selectFarmer": "शेतकरी निवडा",
    "col.none": "कोणतेही संकलन सापडले नाही.",
    "col.receipt": "पावती",
    "rate.title": "दर तक्ता",
    "rate.subtitle": "रक्कम = आधार + (फॅट × फॅट दर) + (एसएनएफ × एसएनएफ दर), प्रति लिटर. नवीनतम लागू तारीख आपोआप लागू होते.",
    "rate.new": "नवीन दर",
    "rate.effectiveDate": "लागू तारीख",
    "rate.base": "आधार दर ₹",
    "rate.fat": "फॅट दर ₹",
    "rate.snf": "एसएनएफ दर ₹",
    "rate.cowRates": "गाय दूध दर",
    "rate.buffaloRates": "म्हैस दूध दर",
    "rate.none": "अद्याप दर नाहीत.",
    "pay.title": "देयके",
    "pay.subtitle": "आगाऊ, वजावट आणि बोनससह शेतकरी देयके तयार करा",
    "pay.generate": "देयक तयार करा",
    "pay.period": "कालावधी",
    "pay.gross": "एकूण",
    "pay.deductions": "वजावटी",
    "pay.net": "निव्वळ",
    "pay.advance": "आगाऊ ₹",
    "pay.deduction": "वजावट ₹",
    "pay.bonus": "बोनस ₹",
    "pay.grossUnpaid": "एकूण (न भरलेले)",
    "pay.collections": "संकलने",
    "pay.netPayable": "निव्वळ देय",
    "pay.none": "अद्याप देयके नाहीत.",
    "pay.receipt": "देयक पावती",
    "rep.title": "अहवाल",
    "rep.subtitle": "संकलन, शेतकरीनिहाय, गुणवत्ता आणि देयक विश्लेषण",
    "rep.totalMilk": "एकूण दूध",
    "rep.totalAmount": "एकूण रक्कम",
    "rep.entries": "नोंदी",
    "rep.summary": "संकलन सारांश",
    "rep.farmerWise": "शेतकरीनिहाय",
    "rep.quality": "फॅट / एसएनएफ",
    "rep.payments": "देयके",
    "rep.daily": "दैनिक",
    "rep.monthly": "मासिक",
    "rep.avgFat": "सरासरी फॅट",
    "rep.avgSnf": "सरासरी एसएनएफ",
    "rep.qualityTrend": "सरासरी फॅट व एसएनएफ कल",
    "rep.paymentReport": "देयक अहवाल",
    "rep.ledger": "शेतकरी खतावणी",
    "rep.ledgerReport": "शेतकरी पेमेंट खतावणी",
    "sms.send": "SMS पाठवा",
    "sms.openApp": "SMS App उघडा",
    "sms.preview": "SMS पूर्वावलोकन",
    "sms.to": "प्रति",
    "sms.note": "पाठवा दाबल्यावर SMS तुमच्या स्वतःच्या SIM व SMS पॅकमधून जाईल.",
    "sms.desktop": "SMS पाठवण्यासाठी कृपया Dairy Hub ERP मोबाईलवर उघडा.",
    "sms.invalid": "या शेतकऱ्याचा वैध मोबाईल नंबर नाही.",
    "users.title": "वापरकर्ता व्यवस्थापन",
    "users.subtitle": "भूमिका-आधारित परवानग्यांसह ऑपरेटर आणि लेखापाल आमंत्रित करा",
    "users.add": "वापरकर्ता जोडा",
    "users.create": "वापरकर्ता तयार करा",
    "users.password": "पासवर्ड",
    "perm.tenant_owner": "सर्व मॉड्यूलमध्ये पूर्ण प्रवेश",
    "perm.operator": "संकलन जोडा · पावत्या प्रिंट करा · शेतकरी पहा",
    "perm.accountant": "देयके · अहवाल · निर्यात · संकलन पहा",
    "act.title": "क्रियाकलाप नोंदी",
    "act.subtitle": "आपल्या केंद्रातील सर्व क्रियांचा लेखापरीक्षण मागोवा",
    "act.none": "अद्याप क्रियाकलाप नाही.",
    "set.title": "सेटिंग्ज",
    "set.companyAppearance": "कंपनी आणि स्वरूप",
    "set.companyName": "कंपनीचे नाव",
    "set.logo": "लोगो URL",
    "set.theme": "थीम",
    "set.light": "उजळ",
    "set.dark": "गडद",
    "set.currency": "चलन",
    "set.printer": "थर्मल प्रिंटर",
    "set.paperWidth": "कागद रुंदी",
    "set.header": "पावती शीर्षक",
    "set.footer": "पावती तळटीप",
    "set.notifications": "सूचना",
    "set.emailActive": "ईमेल (सक्रिय)",
    "set.smsSoon": "एसएमएस (लवकरच)",
    "set.whatsappSoon": "व्हॉट्सअ‍ॅप (लवकरच)",
    "set.pushSoon": "पुश (लवकरच)",
    "receipt.milkTitle": "दूध संकलन पावती",
    "receipt.paymentTitle": "देयक पावती",
    "receipt.receipt": "पावती क्र.",
    "receipt.date": "तारीख",
    "receipt.farmer": "शेतकरी",
    "receipt.type": "प्रकार",
    "receipt.qty": "प्रमाण (लि)",
    "receipt.rate": "दर/लि",
    "receipt.total": "एकूण",
    "receipt.thanks": "धन्यवाद!",
    "receipt.gross": "एकूण रक्कम",
    "receipt.advance": "आगाऊ",
    "receipt.deduction": "वजावट",
    "receipt.bonus": "बोनस",
    "receipt.netPaid": "निव्वळ दिले",
    "receipt.period": "कालावधी",
  },
};

const I18nContext = createContext();
export const useI18n = () => useContext(I18nContext);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => translations[lang]?.[key] ?? translations.en[key] ?? key;

  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
}
